<template>
  <div id="listWidget" class="mb-5">
    <div>
      <InstructionStepsCard
        title="설정 단계"
        :steps="replaceControllerSteps"
      />

      <SettingWarningBox
        message="조명제어기 설정시 한 장치만 활성화 되어 있어야합니다."
      />

      <ControllerModelGuideCard />

      <!-- 연결 버튼 -->
      <div class="action-button-container mb-2">
        <button
          type="button"
          class="btn btn-primary w-100"
          @click="connectionDialog.show"
        >
          <i class="fa-solid fa-link me-2"></i>
          조명제어기 연결
        </button>
      </div>
    </div>

    <hr class="mb-4" v-if="(settingParams && settingParams.name) || lightControllers.length > 0" />

    <h6 class="mb-3" v-if="settingParams && settingParams.name">
      wifi:{{ settingParams.name }}
    </h6>

    <div class="row">
      <ControllerItem
        v-for="(item, index) in lightControllers"
        :key="item._id"
        :index="index"
        :data="item"
        v-on:onDeleteClick="onReceivedDeleteClick"
      />
    </div>

    <!-- ControllerConnectionDialog -->
    <ControllerConnectionDialog
      :visible="connectionDialog.visible"
      title="조명제어기 연결 및 교체"
      actionType="replace"
      okText="교체"
      :needsProvisioning="true"
      :needsWifiInput="true"
      :initialWifiSsid="settingParams.ssid"
      :initialWifiPassword="settingParams.pwd"
      @complete="onConnectionComplete"
      @cancel="connectionDialog.hide"
    />
  </div>
</template>
<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onBeforeMount,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  
} from 'vue';
import { useStore } from 'vuex';
import Axios, { ZoneApi, CtrlApi } from '@/api';

import { useRouter, useRoute ,onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import useModal from '@/common/hooks/useModal';

import ControllerItem from './components/ControllerItem.vue';
import ControllerConnectionDialog from './components/ControllerConnectionDialog.vue';
import InstructionStepsCard from './components/InstructionStepsCard.vue';
import SettingWarningBox from './components/SettingWarningBox.vue';
import ControllerModelGuideCard from './components/ControllerModelGuideCard.vue';
import { SettingConfig } from '@/common/constants';
import useMqttControl from '@/common/hooks/useMqttControl';

const replaceControllerSteps = [
  '조명제어기의 <strong>리셋(RESET) 버튼</strong>을 길게 눌러서 세팅 모드로 진입하시고 LED를 확인하십시오.',
  '<strong>연결 버튼</strong>을 눌러서 조명제어기에 접속하십시오.',
  '연결된 조명제어기에 로컬 wifi 네트워크를 설정해주십시오.',
  '연결된 조명제어기를 서버에 등록',
];

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const route = useRoute();
const props = defineProps(['data']);
const store = useStore();
const account = computed(() => store.getters['account']);
const connectionDialog = reactive(useModal());
const controllers = computed(() => store.getters['home/getLowVersionControllers']);
const zones = computed(() => store.getters['home/getZones']);
const zone = computed(() => store.getters['home/getCurrentZone']);
const controllerZone = computed(() => {
  const controllerId = route.params.id;
  return zones.value.find((z) =>
    z.controllers?.some((c) => c._id === controllerId),
  );
});

const forceRoute = ref(false);

const lightControllers = ref([]);

const settingParams = ref({
  ssid: '',
  pwd: '',
  name: '',
});

const connectedController = ref({
  _id: '',
  version: '',
  model: '',
});

onBeforeMount(() => {  
  emitter.emit('onChangeHeaderSetting',{title:'조명제어기 교체',isShowMenu:false,isShowZone:false,isShowProfile:false,isShowRightHome:true});    
});
onBeforeUnmount(() => {
  connectionDialog.hide();
  emitter.emit('onChangeHeaderSetting',{title:'',isShowMenu:true,isShowZone:true,isShowProfile:true});  
})
onMounted(() => {
  forceRoute.value = false; // 컴포넌트 마운트 시 forceRoute 초기화
  // requestZone();
  console.log(route.params.id);
  console.log('ControllerSettingPage.onMounted => zone : ', zone.value);
});

onBeforeRouteUpdate((to, from) => {
  // 같은 컴포넌트가 재사용될 때 forceRoute 초기화
  forceRoute.value = false;
  return true;
});

watch(zone, () => {
  console.log('ControllerSettingPage.watch => zone : ', zone.value);
});

const setSettingModeOff = async () =>{
  const targetZone = controllerZone.value || zone.value;
  if (!targetZone?._id) return;
  forceRoute.value = true;
  const payload = {isEditMode:false}
  const result = await Axios.patch(
    ZoneApi.UPDATE_ZONE.replace(':id', targetZone._id),
    payload,
  );

  if (result.resultCode === '0000') {
    mqttControl.onPublishZone(targetZone._id,payload)
    await store.dispatch('home/fetchAllZones');
  }
};

const navigateAfterSettingModeLeave = async (to) => {
  const target = to.fullPath || to.path;
  if (to.path === '/settings/select/controller') {
    router.back();
  } else {
    await router.replace(target);
  }
};

onBeforeRouteLeave((to, from) => {
  if (forceRoute.value) {
    forceRoute.value = false;
    connectionDialog.hide();
    return true;
  }

  const targetZone = controllerZone.value || zone.value;
  if (!targetZone?.isEditMode) {
    connectionDialog.hide();
    return true;
  }

  return new Promise((resolve) => {
    if (store.getters['isLoading'] || connectionDialog.visible) {
      emitter.emit('showPopConfirm', {
        description: '세팅이 진행중입니다.\n절차를 중지하고 세팅모드를 종료하시겠습니까?',
        okCallback: async () => {
          await allStopProcess();
          forceRoute.value = true;
          await navigateAfterSettingModeLeave(to);
        },
        cancelCallback: () => {},
      });
      resolve(false);
      return;
    }

    emitter.emit('showPopAlert', {
      title: '세팅 모드',
      description: '세팅모드를 종료하시겠습니까?',
      okCallback: async () => {
        forceRoute.value = true;
        await setSettingModeOff();
        connectionDialog.hide();
        await navigateAfterSettingModeLeave(to);
      },
      cancelCallback: () => {},
    });
    resolve(false);
  });
});

const allStopProcess = async () => {
  emitter.emit('hideLoading', true);
  connectionDialog.hide();
  await setSettingModeOff();
};

// 컨트롤러 교체 :
const onChange = async () => {
  console.log("onChange");
  const request = () => {
    return new Promise(async (resolve, reject) => {
      setTimeout(async () => {
        try {
          console.log(CtrlApi.CHANGE_CTRL.replace(':id', route.params.id));
          console.log(connectedController.value);

          const result = await Axios.patch(
            CtrlApi.CHANGE_CTRL.replace(':id', route.params.id),
            connectedController.value,
          );
          resolve(result);
        } catch (e) {
          resolve(e);
        }
      }, SettingConfig.requestInterval);
    });
  }

  console.log("onChange2");
  return new Promise(async (resolve, reject) => {
    try {
      let result = undefined;
      console.log("onChange3");
      for (let i = 0; i < SettingConfig.requestCount; i++) {
        console.log("onChange4");
        emitter.emit('setLoadingText', i === 0 ? '조명제어기 정보를 교체중입니다.' : `조명제어기 정보를 ${i + 1}번째 교체 시도하고 있습니다.`);
        result = await request();
        if (result?.resultCode === "0000") {
          resolve();
          return;
        }
      }
      
      reject(result?.resultMessage?.length > 0 ? result.resultMessage : "조명제어기 교체에 실패했습니다.");
      emitter.emit('hideLoading', true);
    } catch (e) {
      console.log(e);
      reject("조명제어기 교체 중 오류가 발생하였습니다.")
      emitter.emit('hideLoading', true);
    }
  });
};


// 연결 완료 핸들러
const onConnectionComplete = async (result) => {
  const { controller, connectionType, wifiSsid, wifiPassword } = result;
  
  try {
    emitter.emit('showLoading');
    
    // 연결된 컨트롤러 정보 저장
    connectedController.value = controller;
    
    // WiFi 설정 정보 저장
    settingParams.value.ssid = wifiSsid;
    settingParams.value.pwd = wifiPassword;
    
    // 등록 여부 확인 (기존 onSetting의 등록 확인 로직)
    const ownedZones = zones.value.filter(item => item.ownerId == account.value._id);
    const foundZones = ownedZones.filter(item => item.controllers.find(ctrl => ctrl._id == connectedController.value._id));
    
    if (foundZones.length > 0) {
      emitter.emit('hideLoading', true);
      connectionDialog.hide();
      
      emitter.emit('showPopAlert', {
        title: "wifi 정보 재설정",
        description: "현재 사용하고 있는 조명제어기입니다.\nwifi정보만 재설정 되었습니다.",
        okCallback: setSettingModeOff,
        cancelCallback: setSettingModeOff
      });
      return;
    }
    
    // 교체 등록 실행
    await onChange();
    
    // 성공 시 완료 메시지 및 페이지 이동
    connectionDialog.hide();
    emitter.emit('hideLoading', true);
    
    await store.dispatch('home/fetchAllZones');
    
    forceRoute.value = true;
    emitter.emit('showPopAlert', {
      description: '조명제어기가 교체되었습니다.',
      okCallback: () => router.go(-1),
      cancelCallback: () => router.go(-1),
    });
  } catch (e) {
    emitter.emit('hideLoading', true);
    connectionDialog.hide();
    
    if (e.resultCode === "1001") {
      emitter.emit('showPopAlert', {
        title: "등록된 조명제어기",
        description: "이미 등록된 조명제어기입니다.",
      });
    } else if (e.resultCode == 2001) {
      emitter.emit('showPopAlert', {
        title: "wifi 정보 재설정",
        description: e.resultMessage,
        okCallback: setSettingModeOff,
        cancelCallback: setSettingModeOff
      });
    } else {
      console.log(e);
      showPopAlert(e);
    }
  }
};

const showPopAlert = (msg) => {
  emitter.emit('showPopAlert', {
    description: msg,
  });
};

const onReceivedDeleteClick = () => {
  // ControllerItem에서 사용하는 핸들러 (필요시 구현)
};

</script>

<style scoped>
.action-button-container {
  margin-top: 0;
}

.action-button-container .btn {
  padding: 0.625rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.action-button-container .btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}
</style>
